import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Monitor, Shield, ShieldOff, PhoneOff, CheckCircle2,
  MousePointer2, Loader2, UserCheck
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const ICE_SERVERS = { 
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turns:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
  ],
  iceTransportPolicy: "all"
};

export default function RemoteJoinGuest() {
  const { sessionCode } = useParams();
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const localStreamRef = useRef(null);
  const makingOffer = useRef(false);

  const [step, setStep] = useState("join"); // join | session
  const [guestName, setGuestName] = useState("");
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [controlGranted, setControlGranted] = useState(false);

  // Fetch session info (public endpoint, no auth)
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const baseUrl = window.location.origin;
        const res = await fetch(`${baseUrl}/api/remote-sessions/${sessionCode}/public`);
        if (!res.ok) {
          const data = await res.json();
          setLoadError(data.detail || "Session not found");
          return;
        }
        const data = await res.json();
        setSessionInfo(data);
        setControlGranted(data.control_granted);
      } catch {
        setLoadError("Unable to connect to the server");
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [sessionCode]);

  const createPeerConnection = useCallback((isInitiator) => {
    if (pcRef.current && pcRef.current.signalingState !== "closed") {
      pcRef.current.close();
    }
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ice-candidate", candidate: event.candidate }));
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log("[WebRTC-guest] ICE state:", pc.iceConnectionState);
      if (pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed") {
        toast.info("Connection lost. Please refresh if needed.");
      }
    };

    pc.onconnectionstatechange = () => {
      console.log("[WebRTC-guest] Connection state:", pc.connectionState);
    };

    pcRef.current = pc;
    return pc;
  }, []);

  const pendingCandidates = useRef([]);

  const sendOffer = useCallback(async () => {
    const stream = localStreamRef.current;
    if (!stream || makingOffer.current) return;
    makingOffer.current = true;
    try {
      pendingCandidates.current = [];
      const pc = createPeerConnection(true);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsRef.current?.send(JSON.stringify({ type: "offer", sdp: pc.localDescription }));
    } catch (err) {
      console.error("Offer error:", err);
    } finally {
      makingOffer.current = false;
    }
  }, [createPeerConnection]);

  const handleSignaling = useCallback(async (data) => {
    try {
      if (data.type === "answer") {
        const pc = pcRef.current;
        if (pc && pc.signalingState === "have-local-offer") {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          // Flush buffered ICE candidates
          for (const c of pendingCandidates.current) {
            try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) { console.warn("Stale ICE candidate skipped"); }
          }
          pendingCandidates.current = [];
        }
      } else if (data.type === "ice-candidate" && data.candidate) {
        const pc = pcRef.current;
        if (pc && pc.remoteDescription) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        } else {
          pendingCandidates.current.push(data.candidate);
        }
      } else if (data.type === "control_granted") {
        setControlGranted(true);
        toast.success("Remote control granted");
      } else if (data.type === "control_revoked") {
        setControlGranted(false);
        toast.info("Remote control revoked");
      } else if (data.type === "peer_joined" && data.role === "consultant") {
        // Consultant (re)joined — re-send our screen if we're already sharing
        sendOffer();
      } else if (data.type === "request_offer") {
        sendOffer();
      }
    } catch (err) {
      console.error("Signaling error:", err);
    }
  }, [sendOffer]);

  const joinSession = () => {
    if (!guestName.trim()) { toast.error("Please enter your name"); return; }
    setStep("session");

    // Connect WebSocket — use the current page's host (not env variable) for production compatibility
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsHost = window.location.host;
    const ws = new WebSocket(`${protocol}//${wsHost}/api/ws/remote/${sessionCode}`);

    ws.onopen = () => {
      setConnected(true);
      ws.send(JSON.stringify({ type: "peer_joined", user_id: `guest_${Date.now()}`, role: "client", guest_name: guestName }));
    };
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      handleSignaling(msg);
    };
    ws.onerror = () => { toast.error("Connection error"); };
    ws.onclose = () => { setConnected(false); };
    wsRef.current = ws;
  };

  const startScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { cursor: "always" }, audio: false });
      localStreamRef.current = stream;
      setSharing(true);

      await sendOffer();

      stream.getVideoTracks()[0].onended = () => {
        localStreamRef.current = null;
        setSharing(false);
        toast.info("Screen sharing stopped");
      };

      toast.success("Screen sharing started — your consultant can now see your screen");
    } catch (err) {
      console.error("Screen share error:", err);
      toast.error("Screen sharing was cancelled or denied");
    }
  };

  const stopScreenShare = () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    pcRef.current?.close();
    setSharing(false);
    toast.info("Screen sharing stopped");
  };

  const handleGrantControl = () => {
    setControlGranted(true);
    wsRef.current?.send(JSON.stringify({ type: "control_granted" }));
    toast.success("Remote control granted to consultant");
  };

  const handleRevokeControl = () => {
    setControlGranted(false);
    wsRef.current?.send(JSON.stringify({ type: "control_revoked" }));
    toast.info("Remote control revoked");
  };

  const handleEndSession = () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current?.close();
    wsRef.current?.close();
    setStep("ended");
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  // Error
  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-900 border-slate-800">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-900/50 flex items-center justify-center mx-auto mb-4">
              <PhoneOff className="h-8 w-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Session Unavailable</h2>
            <p className="text-slate-400">{loadError}</p>
            <p className="text-slate-500 text-sm mt-4">Please contact your SAG support representative for a new session link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ended
  if (step === "ended") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-900 border-slate-800">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-900/50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Session Ended</h2>
            <p className="text-slate-400">Thank you! Your remote support session has ended. You can close this window.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // JOIN SCREEN
  if (step === "join") {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="remote-join-guest">
        <Card className="max-w-md w-full bg-slate-900 border-slate-800 shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-blue-900/50 flex items-center justify-center mx-auto mb-4">
                <Monitor className="h-8 w-8 text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">SAG Remote Support</h1>
              <p className="text-slate-400 text-sm">Session Code: <span className="font-mono text-blue-400 font-bold">{sessionCode}</span></p>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-4 mb-6 text-center">
              <p className="text-slate-300 text-sm">
                <strong>{sessionInfo?.consultant_name || "A SAG consultant"}</strong> is ready to help you.
              </p>
              <p className="text-slate-500 text-xs mt-1">Your screen will only be shared after you click "Start Screen Sharing"</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-slate-300 text-sm">Your Name</Label>
                <Input
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Enter your name"
                  className="mt-1 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  onKeyDown={(e) => { if (e.key === "Enter") joinSession(); }}
                  data-testid="guest-name-input"
                  autoFocus
                />
              </div>

              <Button
                onClick={joinSession}
                disabled={!guestName.trim()}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base"
                data-testid="join-session-button"
              >
                <UserCheck className="h-5 w-5 mr-2" /> Join Session
              </Button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-slate-600 text-xs">
                By joining, you agree to share your screen with the SAG support team.<br />
                You maintain full control and can stop sharing at any time.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // SESSION SCREEN (active)
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="remote-session-guest">
      {/* Header */}
      <div className="bg-slate-900/90 backdrop-blur border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Monitor className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">SAG Remote Support</h1>
            <p className="text-sm text-slate-400">Session: {sessionCode} — Connected as {guestName}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={connected ? "bg-emerald-900 text-emerald-300" : "bg-slate-800 text-slate-400"}>
            {connected ? "Connected" : "Connecting..."}
          </Badge>
          {controlGranted && (
            <Badge className="bg-amber-900 text-amber-300">
              <MousePointer2 className="h-3 w-3 mr-1" /> Control Granted
            </Badge>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="max-w-lg w-full bg-slate-900 border-slate-800">
          <CardContent className="p-8 text-center">
            {!sharing ? (
              <>
                <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
                  <Monitor className="h-10 w-10 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Share Your Screen</h2>
                <p className="text-slate-400 mb-6">Allow {sessionInfo?.consultant_name || "the consultant"} to view your screen for troubleshooting.</p>
                <Button onClick={startScreenShare} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="guest-start-screen-share">
                  <Monitor className="h-5 w-5 mr-2" /> Start Screen Sharing
                </Button>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full bg-emerald-900/50 flex items-center justify-center mx-auto mb-6 animate-pulse">
                  <CheckCircle2 className="h-10 w-10 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Screen Sharing Active</h2>
                <p className="text-slate-400 mb-6">{sessionInfo?.consultant_name || "The consultant"} can now see your screen.</p>
                
                {/* Control Toggle */}
                <div className="bg-slate-800 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {controlGranted ? <Shield className="h-5 w-5 text-amber-400" /> : <ShieldOff className="h-5 w-5 text-slate-500" />}
                      <div className="text-left">
                        <p className="text-white font-medium">Remote Control</p>
                        <p className="text-xs text-slate-400">
                          {controlGranted ? "Consultant can control your mouse/keyboard" : "View only — consultant can see but not control"}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant={controlGranted ? "destructive" : "secondary"}
                      size="sm"
                      onClick={controlGranted ? handleRevokeControl : handleGrantControl}
                      data-testid="guest-toggle-control"
                    >
                      {controlGranted ? "Revoke" : "Grant"}
                    </Button>
                  </div>
                </div>

                <Button variant="outline" onClick={stopScreenShare} className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="guest-stop-screen-share">
                  Stop Sharing
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="p-6 flex justify-center">
        <Button variant="destructive" onClick={handleEndSession} className="bg-red-500 hover:bg-red-600" data-testid="guest-end-session">
          <PhoneOff className="h-4 w-4 mr-2" /> End Session
        </Button>
      </div>
    </div>
  );
}
